import type { EncoderFrameSink, ExportRenderConfig, FrameDescriptor } from '@/rendering/export/types'

/**
 * 选取浏览器支持的 WebM `mimeType`（VP9 / VP8 / 通用）。
 */
export function pickWebmMimeType(): string {
  const candidates = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm']
  for (const m of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m)) {
      return m
    }
  }
  return ''
}

/** @returns 是否可用 `MediaRecorder` 录制 WebM */
export function isWebmMediaRecorderSupported(): boolean {
  return pickWebmMimeType() !== ''
}

/**
 * 使用 `MediaRecorder` + `canvas.captureStream(0)` 的过渡编码器。
 *
 * 帧推送节奏由离线管线的 `frameIndex` 循环驱动（每帧 `requestFrame`），进度不以墙钟为准。
 * 像素级确定性仍受浏览器合成与 `captureStream` 限制；高质量路径建议后续接 WebCodecs。
 */
export class MediaRecorderEncoderSink implements EncoderFrameSink {
  private rec: MediaRecorder | null = null
  private stream: MediaStream | null = null
  private captureTrack: MediaStreamTrack | null = null
  private chunks: Blob[] = []

  /**
   * @inheritdoc
   */
  async beginEncoding(
    _config: ExportRenderConfig,
    _totalFrames: number,
    canvas: HTMLCanvasElement,
  ): Promise<void> {
    this.chunks = []
    const mime = pickWebmMimeType()
    if (!mime) {
      throw new Error('当前浏览器不支持 WebM 录制')
    }
    this.stream = canvas.captureStream(0)
    this.captureTrack = this.stream.getVideoTracks()[0] ?? null
    this.rec = new MediaRecorder(this.stream, { mimeType: mime })
    this.rec.ondataavailable = (e) => {
      if (e.data.size) this.chunks.push(e.data)
    }
    await new Promise<void>((resolve, reject) => {
      const r = this.rec
      if (!r) {
        reject(new Error('MediaRecorder not initialized'))
        return
      }
      r.onerror = () => reject(new Error('MediaRecorder failed'))
      r.start(250)
      queueMicrotask(() => resolve())
    })
  }

  /**
   * @inheritdoc
   */
  async consumeFrame(_desc: FrameDescriptor): Promise<void> {
    const t = this.captureTrack
    if (t && 'requestFrame' in t && typeof (t as CanvasCaptureMediaStreamTrack).requestFrame === 'function') {
      ;(t as CanvasCaptureMediaStreamTrack).requestFrame()
    }
  }

  /**
   * @inheritdoc
   */
  async finishEncoding(): Promise<Blob | null> {
    const rec = this.rec
    if (!rec) {
      this.disposeStream()
      return null
    }
    return new Promise((resolve, reject) => {
      rec.onstop = () => {
        const base = (rec.mimeType || 'video/webm').split(';')[0] ?? 'video/webm'
        const blob = new Blob(this.chunks, { type: base })
        this.disposeStream()
        resolve(blob)
      }
      rec.onerror = () => {
        this.disposeStream()
        reject(new Error('MediaRecorder failed'))
      }
      try {
        rec.stop()
      } catch {
        this.disposeStream()
        resolve(null)
      }
    })
  }

  /**
   * @inheritdoc
   */
  async abortEncoding(): Promise<void> {
    try {
      this.rec?.stop()
    } catch {
      /* ignore */
    }
    this.disposeStream()
  }

  /** 停止轨道并清空引用 */
  private disposeStream(): void {
    try {
      this.stream?.getTracks().forEach((tr) => tr.stop())
    } catch {
      /* ignore */
    }
    this.stream = null
    this.captureTrack = null
    this.rec = null
    this.chunks = []
  }
}
